import { supabase } from '../lib/supabase';
import { isDemoMode, getDemoOrgId, getAuthOrgId } from '../utils/demoHelper';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Map project phase to a customer-facing status label
const phaseToStatus = (phase) => {
  const map = {
    planning: 'planning',
    permits: 'permits',
    foundation: 'construction',
    framing: 'construction',
    systems: 'construction',
    finishing: 'construction',
    completed: 'completed',
  };
  return map?.[phase] || 'inquiry';
};

export const customerService = {
  // Get all customers (with derived projectStatus from most recent project)
  async getAll() {
    try {
      console.log('[customerService] Fetching all customers...');
      let query = supabase
        ?.from('customers')
        ?.select('*, projects(id, phase, address, adu_type, budget_total, created_at)')
        ?.order('created_at', { ascending: false });

      // Demo mode: filter by demo org_id directly
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) {
          query = query?.eq('organization_id', demoOrgId);
        }
      }

      const { data, error } = await query;

      console.log('[customerService] Query result:', { data, error });

      if (error) {
        console.error('[customerService] Supabase error:', error);
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          console.log('[customerService] No rows returned (PGRST116)');
          return [];
        } else {
          console.log('[customerService] Data error, returning empty array:', error?.message);
          throw new Error(error?.message || 'Failed to fetch customers');
        }
      }

      // Enrich each customer with a derived projectStatus
      const enriched = (data || [])?.map(customer => {
        const projects = customer?.projects || [];
        // Pick the most recent project's phase as the customer status
        const latestProject = projects?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))?.[0];
        const projectStatus = latestProject ? phaseToStatus(latestProject?.phase) : 'inquiry';
        return { ...customer, project_status: projectStatus };
      });

      const result = toCamelCase(enriched);
      console.log('[customerService] Returning customers:', result?.length, 'items');
      return result;
    } catch (error) {
      console.error('[customerService] Exception in getAll:', error);
      throw error;
    }
  },

  // Get customer by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('customers')
        ?.select('*, projects(id, phase, address, adu_type, budget_total, created_at)')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      if (!data) return null;
      const projects = data?.projects || [];
      const latestProject = projects?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))?.[0];
      const projectStatus = latestProject ? phaseToStatus(latestProject?.phase) : 'inquiry';
      return toCamelCase({ ...data, project_status: projectStatus });
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error;
    }
  },

  // Get projects for a specific customer
  async getProjectsByCustomerId(customerId) {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.select('*')
        ?.eq('customer_id', customerId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching customer projects:', error);
      return [];
    }
  },

  // Create new customer
  async create(customerData) {
    try {
      if (isDemoMode()) {
        throw new Error('Cannot create customers in demo mode. Please sign up for a full account.');
      }

      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      const user = session?.user;

      // Use cached org_id from AuthContext
      const organizationId = getAuthOrgId();
      if (!organizationId) {
        throw new Error('User does not belong to an organization');
      }

      const snakeData = toSnakeCase({
        ...customerData,
        createdBy: user?.id,
        organizationId
      });

      const { data, error } = await supabase
        ?.from('customers')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  },

  // Update customer
  async update(id, customerData) {
    try {
      const snakeData = toSnakeCase(customerData);

      const { data, error } = await supabase
        ?.from('customers')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  },

  // Delete customer
  async delete(id) {
    try {
      const { error } = await supabase?.from('customers')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  },

  // Upload customer avatar photo
  async uploadAvatar(customerId, file) {
    try {
      if (isDemoMode()) {
        throw new Error('Cannot upload photos in demo mode.');
      }

      const fileExt = file?.name?.split('.')?.pop()?.toLowerCase();
      const allowedTypes = ['jpg', 'jpeg', 'png', 'webp'];
      if (!allowedTypes?.includes(fileExt)) {
        throw new Error('Only JPG, PNG, and WebP images are allowed.');
      }

      const filePath = `customers/${customerId}/avatar.${fileExt}`;

      // Upload to project-photos bucket
      const { error: uploadError } = await supabase?.storage
        ?.from('project-photos')
        ?.upload(filePath, file, { upsert: true, contentType: file?.type });

      if (uploadError) throw new Error(uploadError?.message);

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from('project-photos')
        ?.getPublicUrl(filePath);

      const avatarUrl = urlData?.publicUrl;

      // Update customer record
      const { data, error: updateError } = await supabase
        ?.from('customers')
        ?.update({ avatar: avatarUrl, updated_at: new Date()?.toISOString() })
        ?.eq('id', customerId)
        ?.select()
        ?.single();

      if (updateError) throw new Error(updateError?.message);

      return toCamelCase(data);
    } catch (error) {
      console.error('Error uploading customer avatar:', error);
      throw error;
    }
  }
};

